<template>
  <div class="containerRepresentante">
    <!-- Info Banner -->
    <div v-if="!consultaProgresso" class="form-info-banner">
      <i class="fa fa-info-circle"></i>
      <div>
        <p class="form-info-banner-text" v-if="!isInstitucional">
          Nesta seção você irá informar os responsáveis legais, emissores de
          ordem e o responsável pelo acesso da conta PJ.
        </p>

        <p class="form-info-banner-text" v-if="isInstitucional">
          Nesta seção você irá informar os responsáveis legais e emissores de
          ordem.
        </p>
      </div>
    </div>

    <!-- SEÇÃO 1: Outros Responsáveis Legais -->
    <section class="form-section">
      <div class="form-section-header">
        <LabelDisclaimer
          :TamanhoFonteLabel="'H5'"
          :NomeLabel="'Outros responsáveis legais'"
          :Sessao="'Representantes'"
          :SubSessao="'Responsáveis Legais'"
        />
        <p
          class="text-sm text-gray-600 dark:text-gray-400 mt-2"
          v-if="!isInstitucional"
        >
          Além dos sócios e procuradores já informados, você pode adicionar
          outros responsáveis legais.
        </p>

        <p
          class="text-sm text-gray-600 dark:text-gray-400 mt-2"
          v-if="isInstitucional"
        >
          Além dos procuradores já informados, você pode adicionar outros
          responsáveis legais.
        </p>

        <button
          class="btn btn-primary btn-adicionar"
          @click="abrirModalResponsavelLegal"
        >
          <i class="fa fa-plus"></i> Adicionar
        </button>
      </div>

      <div class="form-section-content">
        <DataGrid
          v-if="listaResponsaveisLegais.length > 0"
          :items="listaResponsaveisLegais"
          :columns="gridColumnsResponsaveisLegais"
          :actions="gridActionsResponsaveisLegais"
        />
        <div v-else class="empty-state">
          <i class="fa fa-users"></i>
          <p>Nenhum responsável legal cadastrado</p>
        </div>
      </div>
    </section>

    <!-- SEÇÃO 2: Emissores de Ordens -->
    <section class="form-section">
      <div class="form-section-header">
        <LabelDisclaimer
          :TamanhoFonteLabel="'H5'"
          :NomeLabel="'Emissores de ordens'"
          :Sessao="'Representantes'"
          :SubSessao="'Emissores de Ordem'"
        />
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Informe ao menos um emissor de ordem.
        </p>
        <button
          class="btn btn-primary btn-adicionar"
          @click="abrirModalEmissorOrdem"
        >
          <i class="fa fa-plus"></i> Adicionar
        </button>
      </div>

      <div class="form-section-content">
        <DataGrid
          v-if="listaEmissoresOrdem.length > 0"
          :items="listaEmissoresOrdem"
          :columns="gridColumnsEmissores"
          :actions="gridActionsEmissores"
        />
        <div v-else class="empty-state">
          <i class="fa fa-file-signature"></i>
          <p>Nenhum emissor de ordem cadastrado</p>
        </div>
      </div>
    </section>

    <!-- SEÇÃO 3: Responsável pelo Acesso da Conta PJ -->
    <section v-if="configuracaoResponsavelConta" class="form-section">
      <div class="form-section-header">
        <LabelDisclaimer
          :TamanhoFonteLabel="'H5'"
          :NomeLabel="'Responsável pelo acesso da conta PJ'"
          :Sessao="'Representantes'"
          :SubSessao="'Responsável pela Conta'"
        />
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Defina quem será a pessoa solicitada em futuras validações de
          identidade e envio de documentos. Lembre-se: o e-mail de acesso à
          conta Genial é o e-mail da empresa, informado no cadastro.
        </p>
        <button
          class="btn btn-primary btn-adicionar"
          @click="abrirModalResponsavelConta"
          :disabled="responsavelConta.length >= 1"
        >
          <i class="fa fa-plus"></i> Adicionar
        </button>
      </div>

      <div class="form-section-content">
        <DataGrid
          v-if="responsavelConta.length > 0"
          :items="responsavelConta"
          :columns="gridColumnsResponsavelConta"
          :actions="gridActionsResponsavelConta"
        />
        <div v-else class="empty-state">
          <i class="fa fa-user-shield"></i>
          <p>Nenhum responsável pela conta cadastrado</p>
        </div>
      </div>
    </section>

    <!-- Modais -->
    <ModalResponsavelLegal
      ref="modalResponsavelLegal"
      :visible="modalResponsavelLegalVisible"
      :pessoaEdit="pessoaEditando"
      @close="fecharModalResponsavelLegal"
      @submit="salvarResponsavelLegal"
    />

    <ModalEmissorOrdem
      ref="modalEmissorOrdem"
      :visible="modalEmissorOrdemVisible"
      :pessoasDisponiveis="pessoasDisponiveisParaEmissor"
      :pessoaEdit="pessoaEditando"
      @close="fecharModalEmissorOrdem"
      @submit="salvarEmissorOrdem"
    />

    <ModalResponsavelConta
      ref="modalResponsavelConta"
      :visible="modalResponsavelContaVisible"
      :todasPessoas="todasPessoasDisponiveis"
      :pessoaEdit="pessoaEditando"
      @close="fecharModalResponsavelConta"
      @submit="salvarResponsavelConta"
    />

    <!-- Modal de Edição Original (manter para compatibilidade) -->
    <ModalAddPessoa @update-success="fetchListaRepresentantes" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import api from "@/api/axios";
import DataGrid from "@/components/DataGrid.vue";
import LabelDisclaimer from "@/components/LabelDisclaimer.vue";
import ModalResponsavelLegal from "./ModalResponsavelLegal.vue";
import ModalEmissorOrdem from "./ModalEmissorOrdem.vue";
import ModalResponsavelConta from "./ModalResponsavelConta.vue";
import ModalAddPessoa from "./ModalAddPessoa.vue";
import { TIPO_CLIENTE } from "@/constants/TipoClienteEnum";
import { isCNPJ } from "@/utils/documentoUtils";

function isResponsavelComCNPJ(pessoa) {
  if (!pessoa) return false;
  return pessoa.TipoDocumento === "CNPJ" || isCNPJ(pessoa.CpfCnpj);
}

export default {
  name: "RepresentantesPJ",
  components: {
    DataGrid,
    LabelDisclaimer,
    ModalResponsavelLegal,
    ModalEmissorOrdem,
    ModalResponsavelConta,
    ModalAddPessoa,
  },
  data() {
    return {
      consultaProgresso: true,
      configuracaoEmissorOrdem: false,
      configuracaoResponsavelConta: false,

      // Modais
      modalResponsavelLegalVisible: false,
      modalEmissorOrdemVisible: false,
      modalResponsavelContaVisible: false,
      pessoaEditando: null,
    };
  },
  computed: {
    ...mapGetters({
      listaRepresentantes: "GET_LISTA_REPRESENTANTES_PJ",
      listaSocios: "GET_PJ_INFO_COMPLEMENTO_SOCIOS",
      listaProcuradores: "GET_LISTA_PROCURADORES_PJ",
      getConfiguracoes: "GET_CONFIGURACOES",
    }),
    isInstitucional() {
      return this.user?.TipoCliente === TIPO_CLIENTE.INSTITUCIONAL;
    },
    // Listas filtradas por tipo
    listaResponsaveisLegais() {
      if (!this.listaRepresentantes || !Array.isArray(this.listaRepresentantes))
        return [];
      return this.listaRepresentantes.filter(
        (rep) =>
          rep.Tipo === "Representante" && rep.IsRepresentanteLegal != true,
      );
    },

    listaEmissoresOrdem() {
      if (!this.listaRepresentantes || !Array.isArray(this.listaRepresentantes))
        return [];
      return this.listaRepresentantes.filter(
        (rep) =>
          rep.Tipo === "Emissor de ordem" ||
          rep.Tipo === "Pessoa autorizada emitir ordem",
      );
    },

    temEmissorInvalido() {
      return this.listaEmissoresOrdem.some(
        (item) => item.TipoDocumento === "CNPJ",
      );
    },

    temResponsavelInvalido() {
      return (this.responsavelConta || []).some(isResponsavelComCNPJ);
    },

    responsavelConta() {
      // Buscar em representantes (ON_PessoasVinculadas)
      const responsaveisRep = (this.listaRepresentantes || []).filter(
        (rep) => rep.ResponsavelLogin === true,
      );

      // Buscar em sócios (ON_SociosPessoaJuridica)
      const responsaveisSocios = (this.listaSocios || []).filter(
        (socio) => socio.ResponsavelLogin === true,
      );

      // Combinar e retornar apenas 1
      return [...responsaveisRep, ...responsaveisSocios].slice(0, 1);
    },

    // Pessoas disponíveis para "Já Informado"
    pessoasDisponiveisParaEmissor() {
      return [
        ...(this.listaSocios || []),
        ...(this.listaProcuradores || []),
        ...this.listaResponsaveisLegais,
      ];
    },

    todasPessoasDisponiveis() {
      const todasPessoas = [
        ...(this.listaSocios || []),
        ...(this.listaProcuradores || []),
        ...this.listaResponsaveisLegais,
        ...this.listaEmissoresOrdem,
      ];

      // Deduplicar por documento (CpfCnpj), mantendo apenas o registro com maior Id
      const pessoasPorDocumento = {};

      todasPessoas.forEach((pessoa) => {
        if (!pessoa.CpfCnpj) return;

        const doc = pessoa.CpfCnpj.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

        if (
          !pessoasPorDocumento[doc] ||
          pessoa.Id > pessoasPorDocumento[doc].Id
        ) {
          pessoasPorDocumento[doc] = pessoa;
        }
      });

      return Object.values(pessoasPorDocumento);
    },

    // Colunas das grids
    gridColumnsResponsaveisLegais() {
      const self = this;
      return [
        {
          key: "Nome",
          label: "Nome e E-mail",
          width: "30%",
          formatter: (value, item) => `${item.Nome} - ${item.Email}`,
        },
        {
          key: "CpfCnpj",
          label: "Documento",
          width: "15%",
          formatter: (value) => self.formatCpfCnpj(value),
        },
        {
          key: "Tipo",
          label: "Tipo",
          width: "15%",
          formatter: () => "Responsável legal",
        },
        {
          key: "UsPerson",
          label: "US Person",
          formatter: (value) => (value ? "Sim" : "Não"),
        },
        {
          key: "PoliticamenteExposta",
          label: "Politicamente Exposta",
          formatter: (value) => (value ? "Sim" : "Não"),
        },
        {
          key: "AssinaturaFormatada",
          label: "Assinatura",
        },
      ];
    },

    gridColumnsEmissores() {
      const self = this;
      return [
        {
          key: "Nome",
          label: "Nome e E-mail",
          width: "35%",
          formatter: (value, item) => `${item.Nome} - ${item.Email}`,
        },
        {
          key: "CpfCnpj",
          label: "Documento",
          width: "20%",
          formatter: (value) => self.formatCpfCnpj(value),
        },
        {
          key: "Tipo",
          label: "Tipo",
          width: "20%",
          formatter: () => "Emissor de ordem",
        },
        {
          key: "AssinaturaFormatada",
          label: "Assinatura",
        },
      ];
    },

    gridColumnsResponsavelConta() {
      const self = this;
      return [
        {
          key: "Nome",
          label: "Nome e E-mail",
          width: "40%",
          formatter: (value, item) => `${item.Nome} - ${item.Email}`,
        },
        {
          key: "CpfCnpj",
          label: "Documento",
          width: "25%",
          formatter: (value) => self.formatCpfCnpj(value),
        },
        {
          key: "Tipo",
          label: "Tipo",
          formatter: () => "Responsável pela conta",
        },
      ];
    },

    // Actions das grids
    gridActionsResponsaveisLegais() {
      return [
        {
          label: "Editar",
          icon: "fa fa-edit",
          handler: (item) => this.editarResponsavelLegal(item),
        },
        {
          label: "Excluir",
          icon: "fa fa-trash",
          handler: (item) => this.excluirItem(item, "Responsável legal"),
          variant: "delete",
        },
      ];
    },

    gridActionsEmissores() {
      return [
        {
          label: "Editar",
          icon: "fa fa-edit",
          visible: (item) => !item.PessoaReferenciaGuid,
          handler: (item) => this.editarEmissorOrdem(item),
        },
        {
          label: "Excluir",
          icon: "fa fa-trash",
          handler: (item) => this.excluirItem(item, "Emissor de ordem"),
          variant: "delete",
        },
      ];
    },

    gridActionsResponsavelConta() {
      return [
        {
          label: "Editar",
          icon: "fa fa-edit",
          visible: (item) => item.IsRepresentanteLegal,
          handler: (item) => this.editarResponsavelConta(item),
        },
        {
          label: "Excluir",
          icon: "fa fa-trash",
          handler: (item) => this.excluirItem(item, "Responsável conta"),
          variant: "delete",
        },
      ];
    },
  },

  async mounted() {
    this.$store.commit("SET_PJ_SALVAR_INFO", false);
    await this.fetchListaRepresentantes();
    // Cadastro institucional não possui seção de sócios
    if (!this.isInstitucional) {
      await this.fetchListaSocios();
    }
    await this.fetchListaProcuradores();
    await this.carregarConfiguracoes();

    if (this.listaRepresentantes != null) {
      this.consultaProgresso = false;
    }
  },

  methods: {
    ...mapActions({
      fetchConfiguracao: "FETCH_CONFIGURACOES",
    }),

    formatCpfCnpj(cpfCnpj) {
      if (!cpfCnpj) return "";
      const numeros = cpfCnpj.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
      if (numeros.length === 11) {
        return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      } else if (numeros.length === 14) {
        return numeros.replace(
          /([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})/,
          "$1.$2.$3/$4-$5",
        );
      }
      return cpfCnpj;
    },

    async carregarConfiguracoes() {
      await this.fetchConfiguracao([
        "Exibir pergunta - Responsável pelo login?",
        "Obrigatoriedade Emissor de Ordem",
      ]);

      this.configuracaoResponsavelConta =
        this.getConfiguracoes("Exibir pergunta - Responsável pelo login?")
          ?.Valor === "S";

      this.configuracaoEmissorOrdem =
        this.getConfiguracoes("Obrigatoriedade Emissor de Ordem")?.Valor ===
        "S";
    },

    async fetchListaRepresentantes() {
      await this.$store.dispatch("FETCH_PJ_LISTA_REPRESENTANTES");
    },

    async fetchListaSocios() {
      await this.$store.dispatch("FETCH_PJ_INFO_COMPLEMENTO_SOCIOS");
    },

    async fetchListaProcuradores() {
      await this.$store.dispatch("FETCH_PJ_LISTA_PROCURADORES");
    },

    // Métodos para abrir modais
    abrirModalResponsavelLegal() {
      this.pessoaEditando = null;
      this.modalResponsavelLegalVisible = true;
    },

    abrirModalEmissorOrdem() {
      this.pessoaEditando = null;
      this.modalEmissorOrdemVisible = true;
    },

    abrirModalResponsavelConta() {
      this.pessoaEditando = null;
      if (this.responsavelConta.length >= 1) {
        this.$swal({
          icon: "warning",
          title: "Limite atingido",
          text: "Apenas uma pessoa pode ser o responsável pelo acesso da conta PJ.",
        });
        return;
      }
      this.modalResponsavelContaVisible = true;
    },

    // Métodos para fechar modais
    fecharModalResponsavelLegal() {
      this.modalResponsavelLegalVisible = false;
      this.pessoaEditando = null;
    },

    fecharModalEmissorOrdem() {
      this.modalEmissorOrdemVisible = false;
    },

    fecharModalResponsavelConta() {
      this.modalResponsavelContaVisible = false;
      this.pessoaEditando = null;
    },

    // Métodos para salvar
    async salvarResponsavelLegal(pessoa) {
      try {
        const endpoint = pessoa.Guid
          ? "/v2/PessoaJuridica/representante/atualizar"
          : "/v2/PessoaJuridica/representante/salvar";

        pessoa.Tipo = "Representante";

        await api.post(endpoint, pessoa);

        this.$swal({
          icon: "success",
          title: "Sucesso!",
          text: pessoa.Guid
            ? "Responsável legal atualizado com sucesso!"
            : "Responsável legal adicionado com sucesso!",
        });

        this.fecharModalResponsavelLegal();
        await this.fetchListaRepresentantes();
      } catch (error) {
        if (this.$refs.modalResponsavelLegal) {
          this.$refs.modalResponsavelLegal.resetarLoading();
        }
        this.$swal({
          icon: "error",
          title: "Erro",
          text:
            error.response?.data?.data?.[0] ||
            "Erro ao salvar responsável legal.",
        });
      }
    },

    async salvarEmissorOrdem(data) {
      try {
        if (data.jaInformado) {
          // Adicionar tipo "Emissor de Ordem" a pessoa existente
          const pessoaExistente = this.pessoasDisponiveisParaEmissor.find(
            (p) => p.Guid === data.guid,
          );
          if (pessoaExistente) {
            const { Guid, ...pessoaSemGuid } = pessoaExistente;
            const payload = {
              ...pessoaSemGuid,
              PessoaReferenciaGuid: Guid,
              Tipo: "Pessoa autorizada emitir ordem",
              Assinatura: 1,
            };
            await api.post("/v2/PessoaJuridica/representante/salvar", payload);
          }
        } else {
          // Determinar endpoint: atualizar se tem Guid, salvar se é novo
          const endpoint = data.Guid
            ? "/v2/PessoaJuridica/representante/atualizar"
            : "/v2/PessoaJuridica/representante/salvar";
          await api.post(endpoint, data);
        }

        this.$swal({
          icon: "success",
          title: "Sucesso!",
          text: data.Guid
            ? "Emissor de ordem atualizado com sucesso!"
            : "Emissor de ordem adicionado com sucesso!",
        });

        this.fecharModalEmissorOrdem();
        await this.fetchListaRepresentantes();
      } catch (error) {
        if (this.$refs.modalEmissorOrdem) {
          this.$refs.modalEmissorOrdem.resetarLoading();
        }
        this.$swal({
          icon: "error",
          title: "Erro",
          text:
            error.response?.data?.data?.[0] ||
            "Erro ao adicionar emissor de ordem.",
        });
      }
    },

    async salvarResponsavelConta(data) {
      try {
        if (data.jaInformado) {
          // Buscar a pessoa selecionada pelo Guid no modal
          const pessoaSelecionada = this.todasPessoasDisponiveis.find(
            (p) => p.Guid === data.guid,
          );

          if (pessoaSelecionada) {
            const docNormalizado = pessoaSelecionada.CpfCnpj?.replace(
              /[^A-Za-z0-9]/g,
              "",
            )?.toUpperCase();

            // Buscar pelo DOCUMENTO (CpfCnpj) na lista de sócios
            // (somente para PJ - cadastro institucional não possui sócios)
            const socio = !this.isInstitucional
              ? this.listaSocios?.find(
                  (s) =>
                    s.CpfCnpj?.replace(/[^A-Za-z0-9]/g, "")?.toUpperCase() ===
                    docNormalizado,
                )
              : null;

            if (socio) {
              // Formatar campos numéricos para envio
              const socioFormatado = { ...socio };
              if (socioFormatado.Percentual) {
                const percStr = socioFormatado.Percentual.toString().trim();
                // Só converter formato BR (com vírgula) para EN (com ponto)
                if (percStr.includes(",")) {
                  socioFormatado.Percentual = percStr
                    .replaceAll(".", "")
                    .replaceAll(",", ".");
                }
              }
              if (socioFormatado.OutrosRendimentos) {
                const rendStr =
                  socioFormatado.OutrosRendimentos.toString().trim();
                if (rendStr.includes(",")) {
                  socioFormatado.OutrosRendimentos = rendStr
                    .replace("R$", "")
                    .replaceAll(".", "")
                    .replaceAll(",", ".")
                    .trim();
                } else {
                  socioFormatado.OutrosRendimentos = rendStr
                    .replace("R$", "")
                    .trim();
                }
              }

              const payload = {
                socios: [
                  {
                    ...socioFormatado,
                    ResponsavelLogin: true,
                  },
                ],
              };
              await api.post("/PessoaJuridica/socios", payload);
            } else {
              // É representante/emissor - buscar na lista de representantes pelo documento
              const representante = this.listaRepresentantes?.find(
                (r) =>
                  r.CpfCnpj?.replace(/[^A-Za-z0-9]/g, "")?.toUpperCase() ===
                  docNormalizado,
              );

              if (representante) {
                const payload = {
                  ...representante,
                  ResponsavelLogin: true,
                };
                await api.post(
                  "/v2/PessoaJuridica/representante/atualizar",
                  payload,
                );
              }
            }
          }
        } else {
          // Salvar nova pessoa como responsável (ou atualizar se já tem Guid)
          const endpoint = "/v2/PessoaJuridica/representante/salvar";
          const { guid, ...dadosSemGuid } = data;
          const payload = {
            ...dadosSemGuid,
            ...(guid ? { PessoaReferenciaGuid: guid } : {}),
            ResponsavelLogin: true,
            IsRepresentanteLegal: false,
            Tipo: "Responsável Legal",
            Assinatura: 1,
            UsPerson: false,
            PoliticamenteExposta: false,
          };
          await api.post(endpoint, payload);
        }

        this.$swal({
          icon: "success",
          title: "Sucesso!",
          text: "Responsável pela conta definido com sucesso!",
        });

        this.fecharModalResponsavelConta();
        await this.fetchListaRepresentantes();
        // Cadastro institucional não possui seção de sócios
        if (!this.isInstitucional) {
          await this.fetchListaSocios();
        }
        await this.fetchListaProcuradores();
      } catch (error) {
        if (this.$refs.modalResponsavelConta) {
          this.$refs.modalResponsavelConta.resetarLoading();
        }
        this.$swal({
          icon: "error",
          title: "Erro",
          text:
            error.response?.data?.data?.[0] ||
            "Erro ao definir responsável pela conta.",
        });
      }
    },

    // Editar
    editarResponsavelLegal(item) {
      this.pessoaEditando = { ...item };
      this.modalResponsavelLegalVisible = true;
    },

    editarEmissorOrdem(item) {
      this.pessoaEditando = { ...item };
      this.modalEmissorOrdemVisible = true;
    },

    editarResponsavelConta(item) {
      this.pessoaEditando = { ...item };
      this.modalResponsavelContaVisible = true;
    },

    // Excluir
    async excluirItem(item, tipo) {
      const confirmacao = await this.$swal({
        icon: "warning",
        title: "Confirmar exclusão",
        text: `Deseja realmente excluir ${item.Nome}?`,
        showCancelButton: true,
        confirmButtonText: "Sim, excluir",
        cancelButtonText: "Cancelar",
      });

      if (!confirmacao.isConfirmed) return;

      try {
        // Se for Responsável pela Conta, desmarcar ResponsavelLogin
        if (tipo === "Responsável conta") {
          const docNormalizado = item.CpfCnpj?.replace(
            /[^A-Za-z0-9]/g,
            "",
          )?.toUpperCase();
          const requests = [];

          // Buscar pelo DOCUMENTO na lista de sócios
          // (somente para PJ - cadastro institucional não possui sócios)
          const socio = !this.isInstitucional
            ? this.listaSocios?.find(
                (s) =>
                  s.CpfCnpj?.replace(/[^A-Za-z0-9]/g, "")?.toUpperCase() ===
                  docNormalizado,
              )
            : null;

          if (socio) {
            const socioFormatado = { ...socio };
            if (socioFormatado.Percentual) {
              const percStr = socioFormatado.Percentual.toString().trim();
              if (percStr.includes(",")) {
                socioFormatado.Percentual = percStr
                  .replaceAll(".", "")
                  .replaceAll(",", ".");
              }
            }
            if (socioFormatado.OutrosRendimentos) {
              const rendStr =
                socioFormatado.OutrosRendimentos.toString().trim();
              if (rendStr.includes(",")) {
                socioFormatado.OutrosRendimentos = rendStr
                  .replace("R$", "")
                  .replaceAll(".", "")
                  .replaceAll(",", ".")
                  .trim();
              } else {
                socioFormatado.OutrosRendimentos = rendStr
                  .replace("R$", "")
                  .trim();
              }
            }

            requests.push(
              api.post("/PessoaJuridica/socios", {
                socios: [{ ...socioFormatado, ResponsavelLogin: false }],
              }),
            );
          }

          const representante = this.listaRepresentantes?.find(
            (r) =>
              r.CpfCnpj?.replace(/[^A-Za-z0-9]/g, "")?.toUpperCase() ===
              docNormalizado,
          );

          if (representante) {
            requests.push(
              api.post("/v2/PessoaJuridica/representante/atualizar", {
                ...representante,
                ResponsavelLogin: false,
              }),
            );
          }

          await Promise.all(requests);
        } else {
          // Exclusão normal para outros tipos
          await api.delete(`/PessoaJuridica/representante/excluir`, {
            params: { guid: item.Guid, tipo: item.Tipo },
          });
        }

        this.$swal({
          icon: "success",
          title: "Excluído!",
          text: `${tipo} excluído com sucesso.`,
        });

        await this.fetchListaRepresentantes();
        // Cadastro institucional não possui seção de sócios
        if (!this.isInstitucional) {
          await this.fetchListaSocios();
        }
        await this.fetchListaProcuradores();
      } catch (error) {
        this.$swal({
          icon: "error",
          title: "Erro",
          text: error.response?.data?.data?.[0] || "Erro ao excluir.",
        });
      }
    },

    // Validação do formulário (chamado pelo componente pai ao navegar entre abas)
    async validarFormulario(ignorarMensagens = false) {
      // Validar Responsável pela Conta (se configurado)
      if (this.configuracaoResponsavelConta) {
        // Recarregar dados do store para garantir dados atualizados
        await this.fetchListaRepresentantes();
        // Cadastro institucional não possui seção de sócios
        if (!this.isInstitucional) {
          await this.fetchListaSocios();
        }

        const responsavelContaValido = this.responsavelConta;
        if (!responsavelContaValido || responsavelContaValido.length === 0) {
          if (!ignorarMensagens) {
            this.$swal({
              icon: "error",
              title: "Atenção",
              text: "É obrigatório informar o responsável pelo acesso da conta PJ.",
            });
          }
          return false;
        }
      }

      return true;
    },

    // Validação antes de avançar
    async proximaEtapa() {
      // Bloquear se o responsável pelo login ainda tiver documento legado (CNPJ)
      if (this.temResponsavelInvalido) {
        this.$alertWarning(
          "Documento inválido",
          "O documento CNPJ não é permitido para o responsável pelo login. Informe um CPF válido antes de salvar.",
        );
        return;
      }

      // Bloquear se existir emissor com documento legado (CNPJ)
      if (this.temEmissorInvalido) {
        this.$alertWarning(
          "Documento inválido",
          "O tipo de documento CNPJ não é permitido para o emissor. Selecione CPF, CNH ou RNE antes de salvar.",
        );
        return;
      }

      // Validar Emissor de Ordem (se obrigatório)
      if (
        this.configuracaoEmissorOrdem &&
        this.listaEmissoresOrdem.length === 0
      ) {
        this.$swal({
          icon: "error",
          title: "Atenção",
          text: "É obrigatório informar ao menos um emissor de ordem.",
        });
        return;
      }

      // Validar Responsável pela Conta (se configurado)
      // Verifica se a configuração foi carregada E se é obrigatória
      if (this.configuracaoResponsavelConta) {
        // Recarregar dados do store para garantir dados atualizados
        await this.fetchListaRepresentantes();
        // Cadastro institucional não possui seção de sócios
        if (!this.isInstitucional) {
          await this.fetchListaSocios();
        }

        // Verificar novamente após recarregar
        const responsavelContaValido = this.responsavelConta;
        if (!responsavelContaValido || responsavelContaValido.length === 0) {
          this.$swal({
            icon: "error",
            title: "Atenção",
            text: "É obrigatório informar o responsável pelo acesso da conta PJ.",
          });
          return;
        }
      }

      // Avançar para próxima etapa (tab = 2 é Representantes, próxima é 3)
      this.$store.commit("SET_ETAPA_CADASTRO", 3);
      // Salvar sem notificação
      this.$store.commit("SET_SALVAR_INFO_DES_NOTIFICACAO", true);
      this.$store.commit("SET_PJ_SALVAR_INFO", true);
    },
  },
};
</script>

<style lang="scss" scoped>
.containerRepresentante {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.btn-adicionar {
  margin-top: 1rem;
}

.empty-state {
  padding: 3rem 1rem;
  text-align: center;
  color: #9ca3af;

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 1rem;
  }
}

:global(.dark) .empty-state {
  color: #6b7280;
}
</style>
